import { useCallback, useEffect, useState } from 'react';

import { AgencyLookupsApi } from '@/api';
import { ReligionList } from '@/constants/form_inputs';

export interface IReligionOption {
  label: string;
  value: string;
}

const STATIC_OPTIONS: IReligionOption[] = Object.entries(ReligionList).map(
  ([code, label]) => ({ label: `${code} - ${label}`, value: label }),
);

export function useReligionOptions() {
  const [customOptions, setCustomOptions] = useState<IReligionOption[]>([]);

  const fetchCustom = useCallback(async () => {
    try {
      const res = await AgencyLookupsApi.list('religion');
      const staticValues = new Set(STATIC_OPTIONS.map((o) => o.value));
      const custom = (res.data as string[])
        .filter((v) => !staticValues.has(v))
        .map((v) => ({ label: v, value: v }));
      setCustomOptions(custom);
    } catch {
      // will show only the static options
    }
  }, []);

  useEffect(() => {
    fetchCustom();
  }, [fetchCustom]);

  const addOption = useCallback(async (value: string): Promise<void> => {
    await AgencyLookupsApi.create('religion', value);
    setCustomOptions((prev) => {
      if (prev.some((o) => o.value === value)) return prev;
      return [...prev, { label: value, value }].sort((a, b) =>
        a.label.localeCompare(b.label),
      );
    });
  }, []);

  return {
    items: [...STATIC_OPTIONS, ...customOptions],
    addOption,
  };
}
